import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from '../../../models/utils/uiUtils';
import { NotesStoreKeyTypes, useNotesStore } from '../../entities/LeadView/useNotesStore';
import { FieldKey } from '../../viewModels/form';
import type { IMeme } from '../../viewModels/gamification';
import { FollowUpOptionIds } from '../../viewModels/phonecall';
import { FollowUpImportance, type ILeadFollowUp, type LeadServedSource } from '../../viewModels/queue';
import { useSaveCallNote } from '../data/useSaveCallNote';
import { useSaveCallOutcome } from '../data/useSaveCallOutcome';
import { useSkipLead } from '../data/useSkipLead';
import { isRichTextEmpty } from './noteHtml';
import type { FollowUp, PhoneCallOutcome, PhoneCallOutcomeGroup, WrapupState } from './types';

export type UseWrapupOptions = {
	/** Company id for the lead being wrapped up. Required for manual outcomes and skip routing. */
	companyId: string | undefined;
	/** Phone call id from the dialer. Pass `'manual'` for a manual log call. */
	phoneCallId: string | undefined;
	/** Source attribution from the focused-view's queue advance. */
	source: LeadServedSource;
	/**
	 * The Connected outcome group (from `useOutcomes`). Used as the drill-down
	 * source whenever the rep clicks anything that should resolve to "select
	 * a connected status". Legacy always drills into Connected's children
	 * regardless of which top-level the user clicked.
	 */
	connectedGroup?: PhoneCallOutcomeGroup;
	/** BadLead outcome (from `useOutcomes`). Clicking it flips to skip mode. */
	badLeadOutcome?: PhoneCallOutcome;
	/** Fired after a non-Booked submit succeeds. Booked routes through the deal form path. */
	onSubmitted?: () => void;
};

export type UseWrapupResult = {
	state: WrapupState;
	pickOutcome: (outcome: PhoneCallOutcome) => void;
	pickChild: (child: PhoneCallOutcome) => void;
	back: () => void;
	setNote: (html: string) => void;
	setFollowUp: (followUp: FollowUp | null) => void;
	setDecisionMaker: (id: string | null) => void;
	setCcUserIds: (ids: string[]) => void;
	submit: () => Promise<{ requiresDealForm: boolean; meme?: IMeme }>;
	canSave: boolean;
	saveDisabledReason: string | null;
	isSaving: boolean;
};

/**
 * Booked-class outcomes route through the deal form instead of saving in
 * place. Mirrors the legacy `QueueNoteEditor.onSaveClick` early-return on
 * `[FieldKey.Booked]` (`aida/components/QueueNoteEditor/index.tsx:247`),
 * extended to cover the two reschedule paths.
 */
const BOOKED_OUTCOMES: ReadonlySet<string> = new Set<string>([
	FieldKey.Booked,
	FieldKey.NewDemo,
	FieldKey.AppointmentRescheduled,
]);

const initialState: WrapupState = {
	step: 'pick-outcome',
	mode: 'wrapup',
	noteHtml: '',
	followUp: null,
	decisionMakerId: null,
	ccUserIds: [],
};

const IMPORTANCE_MAP: Record<'low' | 'medium' | 'high', FollowUpImportance> = {
	low: FollowUpImportance.Low,
	medium: FollowUpImportance.Medium,
	high: FollowUpImportance.High,
};

/** Debounce window for writing the note draft back to `useNotesStore`. Matches legacy (1000ms). */
const DRAFT_DEBOUNCE_MS = 1000;

/**
 * Wrap-up state machine.
 *
 * Steps: `pick-outcome → (pick-child-outcome | note) → submit`. The
 * top-level `pick-outcome` step is fed the legacy flat `outcomeShortcuts`
 * list (curated leaves, not raw groups). When the rep clicks the
 * `Connected` entry, we drill into the `connectedGroup` from the hook
 * options — never into whatever top-level group they happened to click,
 * matching the legacy `ConnectedOptions` panel.
 *
 * Modes: `'wrapup'` (default) saves an outcome; `'skip'` (entered when the
 * rep picks BadLead from either the top-level or drill-down) routes the
 * Save through the `useSkipLead` mutation instead and the button label
 * flips to "Save & Skip".
 *
 * On submit, Booked-class outcomes (Booked / NewDemo / AppointmentRescheduled)
 * short-circuit and return `requiresDealForm: true` so the parent panel can
 * switch the drawer to the deal-form surface instead of saving in place.
 *
 * `canSave` mirrors the legacy `QueueNoteEditor` gating
 * (`aida/components/QueueNoteEditor/index.tsx:167-185`): an outcome must be
 * selected, and `requireNote` outcomes must have non-empty content.
 *
 * Note draft persistence: hydrates `noteHtml` per-company from `useNotesStore`
 * on companyId change (matches the legacy queue's per-lead draft restore at
 * `QueueNoteEditor/index.tsx:108-114`) and writes back debounced at
 * `DRAFT_DEBOUNCE_MS` (matches legacy 1000ms). On a successful non-skip save
 * the per-company draft is cleared (`history.clear()` in legacy at
 * `QueueNoteEditor/index.tsx:290`).
 */
export function useWrapup(opts: UseWrapupOptions): UseWrapupResult {
	const [state, setState] = useState<WrapupState>(initialState);
	const saveCallNoteMutation = useSaveCallNote();
	const saveOutcomeMutation = useSaveCallOutcome();
	const skipLeadMutation = useSkipLead();
	const notesStore = useNotesStore(NotesStoreKeyTypes.Notes);

	// Track which company we've hydrated for so a remount or a companyId
	// change kicks off a fresh hydration. The store stores raw rich-text
	// content state in `content` and a flat `plain` string; we round-trip
	// the focused-view's HTML draft through `plain` (the `RichContentDocumentEditor`
	// is constructed via `createRichContentEditorStateWithText`, which accepts
	// a string, so HTML round-trips losslessly enough for draft restore).
	const hydratedCompanyIdRef = useRef<string | null>(null);

	useEffect(() => {
		const cid = opts.companyId;
		if (!cid) return;
		if (hydratedCompanyIdRef.current === cid) return;
		hydratedCompanyIdRef.current = cid;
		const draft = notesStore?.state?.[cid];
		if (draft?.plain) {
			setState(prev => ({ ...prev, noteHtml: draft.plain }));
		}
	}, [opts.companyId, notesStore]);

	// Debounced writer for the per-company draft. Recreate when companyId
	// changes so the closure captures the right key.
	const writeDraft = useMemo(() => {
		const cid = opts.companyId;
		if (!cid) {
			return (_html: string): void => {
				/* no-op when no company id */
			};
		}
		const fn = (html: string): void => {
			const stripped = html.replace(/<[^>]+>/g, '').trim();
			if (!stripped) return;
			const next = { ...(notesStore?.state ?? {}) };
			next[cid] = {
				// `IRawRichTextContentState` is unused by the focused-view's draft
				// restore; we cast `plain` to `any` for the content slot to keep
				// the legacy shape compatible without round-tripping HTML through
				// the legacy raw rich-text format.
				content: html as unknown as ReturnType<typeof Object>,
				plain: html,
			} as (typeof next)[string];
			notesStore?.update(next);
		};
		return debounce(fn, DRAFT_DEBOUNCE_MS);
	}, [opts.companyId, notesStore]);

	const isBadLead = useCallback(
		(o: PhoneCallOutcome | undefined): boolean => {
			if (!o) return false;
			if (o.name === FieldKey.BadLead) return true;
			if (opts.badLeadOutcome && o.id === opts.badLeadOutcome.id) return true;
			return false;
		},
		[opts.badLeadOutcome]
	);

	const pickOutcome = useCallback(
		(outcome: PhoneCallOutcome) => {
			setState(prev => {
				// BadLead from the top-level: flip to skip mode, jump straight
				// to the note step, no drill-down. Mirrors legacy
				// `ConnectedOptions.onStatusOptionClick`.
				if (isBadLead(outcome)) {
					return {
						...prev,
						step: 'note',
						mode: 'skip',
						selectedGroup: undefined,
						selectedOutcome: outcome,
					};
				}
				// Connected: drill into the connectedGroup from hook options
				// (NOT into the clicked outcome's children). The drill-down
				// list is filtered to exclude BadLead so the rep can't pick
				// it twice.
				if (outcome.name === FieldKey.Connected && opts.connectedGroup) {
					const filtered: PhoneCallOutcomeGroup = {
						...opts.connectedGroup,
						children: (opts.connectedGroup.children ?? []).filter(c => !isBadLead(c)),
					};
					return {
						...prev,
						step: 'pick-child-outcome',
						mode: 'wrapup',
						selectedGroup: filtered,
						selectedOutcome: undefined,
					};
				}
				// Booked-class leaf shortcut: jump straight to the deal form,
				// bypassing the note step. Mirrors legacy queue behavior — the
				// rep is sent to deal-creation on pick, not on save.
				if (BOOKED_OUTCOMES.has(outcome.name)) {
					return {
						...prev,
						step: 'deal-form',
						mode: 'wrapup',
						selectedGroup: undefined,
						selectedOutcome: outcome,
					};
				}
				// Leaf shortcut: jump straight to the note step.
				return {
					...prev,
					step: 'note',
					mode: 'wrapup',
					selectedGroup: undefined,
					selectedOutcome: outcome,
				};
			});
		},
		[isBadLead, opts.connectedGroup]
	);

	const pickChild = useCallback(
		(child: PhoneCallOutcome) => {
			setState(prev => {
				if (isBadLead(child)) {
					return {
						...prev,
						step: 'note',
						mode: 'skip',
						selectedOutcome: child,
					};
				}
				// Booked-class child: route directly to the deal form (no note step).
				if (BOOKED_OUTCOMES.has(child.name)) {
					return {
						...prev,
						step: 'deal-form',
						mode: 'wrapup',
						selectedOutcome: child,
					};
				}
				return { ...prev, step: 'note', mode: 'wrapup', selectedOutcome: child };
			});
		},
		[isBadLead]
	);

	const back = useCallback(() => {
		setState(prev => {
			if (prev.step === 'note' || prev.step === 'deal-form') {
				if (prev.selectedGroup && prev.selectedGroup.children && prev.selectedGroup.children.length > 0) {
					return {
						...prev,
						step: 'pick-child-outcome',
						mode: 'wrapup',
						selectedOutcome: undefined,
					};
				}
				return {
					...prev,
					step: 'pick-outcome',
					mode: 'wrapup',
					selectedGroup: undefined,
					selectedOutcome: undefined,
				};
			}
			if (prev.step === 'pick-child-outcome') {
				return {
					...prev,
					step: 'pick-outcome',
					mode: 'wrapup',
					selectedGroup: undefined,
					selectedOutcome: undefined,
				};
			}
			return prev;
		});
	}, []);

	const setNote = useCallback(
		(html: string) => {
			setState(prev => ({ ...prev, noteHtml: html }));
			writeDraft(html);
		},
		[writeDraft]
	);

	const setFollowUp = useCallback((followUp: FollowUp | null) => {
		setState(prev => ({ ...prev, followUp }));
	}, []);

	const setDecisionMaker = useCallback((id: string | null) => {
		setState(prev => ({ ...prev, decisionMakerId: id }));
	}, []);

	const setCcUserIds = useCallback((ids: string[]) => {
		setState(prev => ({ ...prev, ccUserIds: ids }));
	}, []);

	const noteIsEmpty = useMemo(() => isRichTextEmpty(state.noteHtml), [state.noteHtml]);

	const saveDisabledReason = useMemo(() => {
		if (!state.selectedOutcome) {
			return 'You must select an outcome for the call';
		}
		if (state.selectedOutcome.requireNote && noteIsEmpty) {
			return "The outcome you've selected requires a note";
		}
		return null;
	}, [state.selectedOutcome, noteIsEmpty]);

	const canSave = saveDisabledReason === null;

	const submit = useCallback(async (): Promise<{ requiresDealForm: boolean; meme?: IMeme }> => {
		if (!state.selectedOutcome) {
			throw new Error('useWrapup.submit: no outcome selected');
		}
		if (!opts.companyId) {
			throw new Error('useWrapup.submit: companyId is required');
		}

		const followUpPayload = followUpToPayload(state.followUp);
		const hasNoteContent = !noteIsEmpty;
		const hasCcUsers = (state.ccUserIds?.length ?? 0) > 0;

		// 1. Save the note FIRST (mirroring legacy `QueueNoteEditor.onSaveClick`
		//    at `aida/components/QueueNoteEditor/index.tsx:255-263` and
		//    `QueueViewModel.saveSkipReason` at `viewModels/queue.ts:510-517`):
		//    a separate note record is POSTed with the rich-text content and the
		//    CC users wired into `referencedEntities.users` so the server's note
		//    -CC routing kicks in. We capture the resulting `noteId` and thread
		//    it into the downstream outcome / skip save.
		//
		//    Skip when there's nothing to save — no content AND no CCs — so we
		//    don't create empty note records for a bare outcome click.
		let noteId = '';
		if (hasNoteContent || hasCcUsers) {
			const savedNote = await saveCallNoteMutation.mutateAsync({
				phoneCallId: opts.phoneCallId ?? 'manual',
				companyId: opts.companyId,
				contentHtml: state.noteHtml,
				ccUserIds: state.ccUserIds,
				isForOutcome: true,
			});
			noteId = savedNote.id ?? '';
		}

		// 2a. Skip path: route through the lead-skip endpoint, not the outcome
		// endpoint. Booked-class short-circuit doesn't apply here because
		// BadLead is never a Booked outcome. The skip endpoint returns a
		// `meme` payload (legacy `QueueViewModel.saveSkipReason`); thread it
		// through to the submit result so the caller can render the overlay.
		if (state.mode === 'skip') {
			const skipMeme = await skipLeadMutation.mutateAsync({
				companyId: opts.companyId,
				followUp: followUpPayload,
				currentCallId: opts.phoneCallId ?? 'manual',
				outcomeId: state.selectedOutcome.id,
				skipReason: state.selectedOutcome.name,
				leadServedSource: opts.source,
				noteId: noteId || undefined,
			});
			opts.onSubmitted?.();
			return { requiresDealForm: false, meme: skipMeme ?? undefined };
		}

		// 2b. Booked-class outcomes are routed directly to the deal form on
		// pick (see `pickOutcome` / `pickChild`); they never reach submit
		// in the normal flow. We retain `requiresDealForm: true` as a
		// defensive guard in case a caller invokes submit() before the
		// state machine transitions — the parent's deal-form path handles
		// it gracefully either way.
		if (BOOKED_OUTCOMES.has(state.selectedOutcome.name)) {
			return { requiresDealForm: true };
		}

		// 2c. Build optional `customProperties` to pipe through to the save mutation.
		// Mirrors `QueueNoteEditor.onSaveClick` (`aida/components/QueueNoteEditor/index.tsx:271`)
		// for `decisionMaker`. `ccUserIds` are NOT threaded through customProperties —
		// they ride on the note save above via `referencedEntities.users`, which is
		// what the server actually reads for CC routing.
		const customProperties: Record<string, unknown> = {};
		if (state.decisionMakerId) {
			customProperties.decisionMaker = state.decisionMakerId;
		}

		const meme = await saveOutcomeMutation.mutateAsync({
			phoneCallId: opts.phoneCallId ?? 'manual',
			companyId: opts.companyId,
			noteId,
			outcomeId: state.selectedOutcome.id ?? '',
			followUp: followUpPayload,
			doNotContact: false,
			source: opts.source,
			customProperties: Object.keys(customProperties).length > 0 ? customProperties : undefined,
		});

		// Clear the per-company draft on a successful save (legacy:
		// `QueueNoteEditor/index.tsx:290 history?.clear()`). We don't clear on
		// the skip path — legacy doesn't either. Scope the wipe to JUST this
		// company so other companies' in-progress drafts survive: the underlying
		// `useNotesStore.clear()` deletes the entire `Notes` key.
		if (opts.companyId && notesStore) {
			const next = { ...(notesStore.state ?? {}) };
			delete next[opts.companyId];
			notesStore.update(next);
		}

		opts.onSubmitted?.();
		return { requiresDealForm: false, meme };
	}, [
		state.selectedOutcome,
		state.followUp,
		state.mode,
		state.decisionMakerId,
		state.ccUserIds,
		state.noteHtml,
		noteIsEmpty,
		opts,
		saveCallNoteMutation,
		saveOutcomeMutation,
		skipLeadMutation,
		notesStore,
	]);

	return {
		state,
		pickOutcome,
		pickChild,
		back,
		setNote,
		setFollowUp,
		setDecisionMaker,
		setCcUserIds,
		submit,
		canSave,
		saveDisabledReason,
		isSaving: saveCallNoteMutation.isPending || saveOutcomeMutation.isPending || skipLeadMutation.isPending,
	};
}

/**
 * Translate the wrap-up's `FollowUp` shape into the payload `useSaveCallOutcome`
 * expects (either a `FollowUpOptionIds` shortcut or an `ILeadFollowUp` record).
 */
function followUpToPayload(followUp: FollowUp | null): FollowUpOptionIds | ILeadFollowUp | null {
	if (!followUp) return null;
	if (followUp.kind === 'option') {
		return followUp.optionId as FollowUpOptionIds;
	}
	const scheduled: ILeadFollowUp = {
		scheduled: true,
		followUpDate: followUp.date,
		importance: IMPORTANCE_MAP[followUp.importance],
		name: followUp.name,
	};
	return scheduled;
}
